import {
  isReturnsSettingsTab,
  RETURNS_SETTINGS_TAB_BY_LEGACY_PATH,
  RETURNS_SETTINGS_TAB_QUERY_PARAM,
  type ReturnsSettingsTab,
} from "../flat-team-settings/returns/returns-settings-tab";
import { RETURNS_CONSOLIDATED_SETTINGS_PATH } from "../flat-team-settings/returns/settings-paths";
import { getSettingCatalogGroup } from "./index";
import { settingsWrapperManifest } from "./settings-wrapper-manifest.generated";
import type {
  SettingAvailabilityContext,
  SettingGroupFullPageTarget,
} from "./types";

const TAB_DIRECTORY = /^returns\/([^/]+)\//;

let tabByGroupId: ReadonlyMap<string, ReturnsSettingsTab> | undefined;
let tabBySettingId: ReadonlyMap<string, ReturnsSettingsTab> | undefined;

function tabOfFile(file: string): ReturnsSettingsTab | undefined {
  const directory = TAB_DIRECTORY.exec(file)?.[1] ?? null;
  return isReturnsSettingsTab(directory) ? directory : undefined;
}

/** A group's wrappers can span tabs; the tab holding most of them wins. */
function buildTabByGroupId(): ReadonlyMap<string, ReturnsSettingsTab> {
  const countsByGroupId = new Map<string, Map<ReturnsSettingsTab, number>>();
  const count = (groupId: string, file: string) => {
    const tab = tabOfFile(file);
    if (!tab) {
      return;
    }
    const counts =
      countsByGroupId.get(groupId) ?? new Map<ReturnsSettingsTab, number>();
    counts.set(tab, (counts.get(tab) ?? 0) + 1);
    countsByGroupId.set(groupId, counts);
  };
  for (const section of settingsWrapperManifest.sections) {
    count(section.id, section.file);
  }
  for (const setting of settingsWrapperManifest.settings) {
    if (setting.sectionId) {
      count(setting.sectionId, setting.file);
    }
  }

  const result = new Map<string, ReturnsSettingsTab>();
  for (const [groupId, counts] of countsByGroupId) {
    const [top] = [...counts].sort(([, a], [, b]) => b - a);
    if (top) {
      result.set(groupId, top[0]);
    }
  }
  return result;
}

function buildTabBySettingId(): ReadonlyMap<string, ReturnsSettingsTab> {
  const result = new Map<string, ReturnsSettingsTab>();
  for (const setting of settingsWrapperManifest.settings) {
    const tab = tabOfFile(setting.file);
    if (tab && !result.has(setting.id)) {
      result.set(setting.id, tab);
    }
  }
  return result;
}

/** The setting's own tab when given, since a split group's majority tab may not hold it. */
export function getReturnsSettingsTabForGroup(
  groupId: string,
  settingId?: string,
): ReturnsSettingsTab | undefined {
  tabByGroupId ??= buildTabByGroupId();
  tabBySettingId ??= buildTabBySettingId();
  return (
    (settingId && tabBySettingId.get(settingId)) || tabByGroupId.get(groupId)
  );
}

export function getReturnsSettingsTabPath(tab: ReturnsSettingsTab): string {
  return `${RETURNS_CONSOLIDATED_SETTINGS_PATH}?${RETURNS_SETTINGS_TAB_QUERY_PARAM}=${tab}`;
}

/** A flow editor or other page under an old one; the new page links out to it too. */
function isNestedPage(path: string): boolean {
  return path.includes("/") && !(path in RETURNS_SETTINGS_TAB_BY_LEGACY_PATH);
}

/** The group's tab on the returns page when the catalog names the old page it replaced. */
export function resolveReturnsSettingsFullPageTarget({
  groupId,
  fullPageTarget,
}: {
  groupId: string;
  fullPageTarget: SettingGroupFullPageTarget | undefined;
}): SettingGroupFullPageTarget | undefined {
  const tab = getReturnsSettingsTabForGroup(groupId);
  if (
    !tab ||
    fullPageTarget?.type !== "settings" ||
    isNestedPage(fullPageTarget.path)
  ) {
    return fullPageTarget;
  }
  return { type: "settings", path: getReturnsSettingsTabPath(tab) };
}

export interface ReturnsSettingsLocations {
  newPage: { path: string; tab: ReturnsSettingsTab };
}

/** Where on the returns page a merchant finds the group. */
export function getReturnsSettingsLocations({
  groupId,
  context,
}: {
  groupId: string;
  context: SettingAvailabilityContext | undefined;
}): ReturnsSettingsLocations | undefined {
  const tab = getReturnsSettingsTabForGroup(groupId);
  const fullPageTarget = getSettingCatalogGroup(groupId)?.fullPageTarget;
  if (
    !tab ||
    fullPageTarget?.type !== "settings" ||
    isNestedPage(fullPageTarget.path)
  ) {
    return undefined;
  }
  return { newPage: { path: getReturnsSettingsTabPath(tab), tab } };
}

export const RETURNS_SETTINGS_LOCATIONS_GUIDANCE =
  'When a result has "returnsSettingsLocations" and you say where to find it, name the tab on the Returns settings page (newPage.tab), not the old settings page the result\'s path points at.';
